import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { MOCK_STATUS_PATH, useCartExtras } from '../../store/cartExtras';
import { useStore } from '../../store/store';
import { Chrome } from '../chrome/Chrome';
import '../cart/cart.css';

/**
 * STEP 4 — Checkout interstitial. The real checkout is Shopify's native page;
 * this screen only bridges to it: while the cart handoff runs (zero old _rpl
 * lines → /cart/add.js batches → location = '/checkout') it shows a
 * "Redirecting to secure checkout" state. In mock mode it fakes the process
 * for 1.5s then routes to the demo status page.
 */
export function CheckoutScreen() {
  const setStep = useStore((s) => s.setStep);
  const phase = useCartExtras((s) => s.phase);
  const notice = useCartExtras((s) => s.notice);
  const runHandoff = useCartExtras((s) => s.runHandoff);
  const retryHandoff = useCartExtras((s) => s.retryHandoff);
  const cancelCheckout = useCartExtras((s) => s.cancelCheckout);
  const navigate = useNavigate();

  useEffect(() => {
    if (phase === 'handoff') {
      void runHandoff({ onMockDone: () => navigate(MOCK_STATUS_PATH) });
    } else if (phase === 'idle') {
      // Deep link / refresh onto step 4 with no active checkout — bounce back.
      setStep(2);
    }
  }, [phase, runHandoff, navigate, setStep]);

  const failed = phase === 'error';

  return (
    <Chrome step={3} headerRight={<span>🔒 Secure checkout</span>}>
      <div className="handoff">
        <span className="handoff__lock" aria-hidden="true">
          🔒
        </span>
        {failed ? (
          <>
            <div className="handoff__title">We couldn&rsquo;t open the checkout</div>
            <p className="handoff__body">
              {notice?.message ??
                "Something interrupted the handover to our secure checkout. Try again. Nothing has been charged."}
            </p>
            <div className="handoff__actions">
              <button type="button" className="btn btn--secondary" onClick={cancelCheckout}>
                ← Back to cart
              </button>
              <button type="button" className="btn btn--primary" onClick={retryHandoff}>
                Try again
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="spinner" role="status" aria-label="Redirecting" />
            <div className="handoff__title">Redirecting to secure checkout…</div>
            <p className="handoff__body">
              {api.isMock
                ? 'Demo mode: simulating the Shopify cart handoff, then showing the order status page.'
                : 'Almost there, payment is the last step. Sending your prints to our secure checkout… don’t refresh.'}
            </p>
          </>
        )}
      </div>
    </Chrome>
  );
}
